import React, { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createOrganizer, addAuthorizationEmail } from '../apiEndpoints';
import { Input, Textarea } from '@heroui/input';
import { addToast } from '@heroui/toast';
import { GoogleMap, useJsApiLoader, Autocomplete, Marker } from '@react-google-maps/api';
import Instagram from '@/icons/Instagram';
import Twitter from '@/icons/Twitter';
import Linkedin from '@/icons/Linkedin';
import Facebook from '@/icons/Facebook';
import Avatar from './Avatar';
import { useNavigate } from 'react-router-dom';
import {
    FlatSection,
    AboutSectionIcon,
    VisualsSectionIcon,
    LinksSectionIcon,
    AccessSectionIcon,
} from './OrganizerProfileSections';

import { btnAccent, card, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

type PendingAuthEmail = { email: string; role: 'admin' | 'eventmanager' | 'usher' };

const heroInput = {
    inputWrapper: 'h-14 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
};
const heroTextarea = {
    inputWrapper: 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
};
const errorText = 'mt-1.5 text-xs font-medium text-[#E53935] dark:text-red-400';
const smallPill = 'inline-flex h-9 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors';

export default function CreateOrganizationForm() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [avatar, setAvatar] = useState<string | null>(null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [banner, setBanner] = useState<string | null>(null);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [orgName, setOrgName] = useState('');
    const [profileUrl, setProfileUrl] = useState('');
    const [about, setAbout] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [instagram, setInstagram] = useState('');
    const [twitter, setTwitter] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [facebook, setFacebook] = useState('');
    const [error, setError] = useState('');
    const [touched, setTouched] = useState<{ [k: string]: boolean }>({});

    // Authorization emails can't be sent to the API until the organization exists,
    // so they're staged locally and flushed right after creation succeeds.
    const [pendingAuthEmails, setPendingAuthEmails] = useState<PendingAuthEmail[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [newRole, setNewRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingRole, setEditingRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const [accessError, setAccessError] = useState('');

    // Google Maps state
    const [lat, setLat] = useState(34.0522);
    const [lng, setLng] = useState(-118.2437);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const [addressLoading, setAddressLoading] = useState(false);
    const [lastFetchedCoords, setLastFetchedCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const mapContainerStyle = { width: '100%', height: '220px', borderRadius: '16px' };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const bannerInputRef = useRef<HTMLInputElement>(null);

    const libraries: ("places")[] = ['places'];
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries,
    });

    function validate() {
        const errors: { [k: string]: string } = {};
        if (!orgName) errors.orgName = 'Organization name is required';
        if (!email) errors.email = 'Email is required';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errors.email = 'Invalid email';
        return errors;
    }
    const errors = validate();

    const mutation = useMutation({
        mutationFn: async () => {
            const formData = new FormData();
            formData.append('orgName', orgName);
            formData.append('profileUrl', profileUrl);
            formData.append('about', about);
            formData.append('email', email);
            formData.append('phone', phone);
            formData.append('address', address);
            formData.append('lat', lat.toString());
            formData.append('lng', lng.toString());
            formData.append('instagram', instagram);
            formData.append('twitter', twitter);
            formData.append('linkedin', linkedin);
            formData.append('facebook', facebook);

            if (avatarFile) formData.append('avatar', avatarFile);
            if (bannerFile) formData.append('banner', bannerFile);

            return createOrganizer(formData);
        },
        onSuccess: async (res: any) => {
            const newOrgId = res?.data?._id || res?.data?.id || res?._id || res?.id;

            addToast({
                title: 'Success',
                description: 'Organization created successfully!',
                color: 'success',
            });
            setError('');
            queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });

            if (newOrgId && pendingAuthEmails.length > 0) {
                await Promise.allSettled(
                    pendingAuthEmails.map(entry => addAuthorizationEmail(newOrgId, entry.email, entry.role))
                );
            }

            navigate('/organizations/events');
        },
        onError: (err: any) => {
            setError(err?.response?.data?.message || err?.message || 'Failed to create organization. Please try again.');
        },
    });

    function attemptCreate() {
        setTouched({ orgName: true, email: true });
        if (Object.keys(errors).length > 0) return;
        mutation.mutate();
    }

    function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            setAvatar(URL.createObjectURL(file));
        }
    }
    function handleBannerChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setBannerFile(file);
            setBanner(URL.createObjectURL(file));
        }
    }
    function handleRemoveAvatar() {
        setAvatar(null);
        setAvatarFile(null);
    }
    function handleRemoveBanner() {
        setBanner(null);
        setBannerFile(null);
    }

    function handleAddAuthEmail() {
        setAccessError('');
        if (!newEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) {
            setAccessError('Enter a valid email address');
            return;
        }
        if (pendingAuthEmails.some(entry => entry.email.toLowerCase() === newEmail.toLowerCase())) {
            setAccessError('This email has already been added');
            return;
        }
        setPendingAuthEmails(prev => [...prev, { email: newEmail, role: newRole }]);
        setNewEmail('');
        setNewRole('usher');
    }
    function handleRemoveAuthEmail(index: number) {
        setPendingAuthEmails(prev => prev.filter((_, i) => i !== index));
        if (editingIndex === index) setEditingIndex(null);
    }
    function handleSaveAuthEmailRole(index: number) {
        setPendingAuthEmails(prev => prev.map((entry, i) => (i === index ? { ...entry, role: editingRole } : entry)));
        setEditingIndex(null);
    }

    // Google Maps event handlers
    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }
    function onPlaceChanged() {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLat(newLat);
                setLng(newLng);
                setAddress(place.formatted_address);
                if (map) {
                    map.panTo({ lat: newLat, lng: newLng });
                }
            }
        }
    }
    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }
    async function fetchAddressFromCoords(mapLat: number, mapLng: number) {
        setAddressLoading(true);
        fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${mapLat},${mapLng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
        )
            .then(response => response.json())
            .then(data => {
                if (data.results && data.results[0]) {
                    setAddress(data.results[0].formatted_address);
                }
            })
            .catch(() => {
                // Optionally handle error
            })
            .finally(() => {
                setAddressLoading(false);
            });
    }
    function handleMapIdle() {
        if (map) {
            const center = map.getCenter();
            const newLat = center.lat();
            const newLng = center.lng();
            if (!lastFetchedCoords || Math.abs(newLat - lastFetchedCoords.lat) > 0.00001 || Math.abs(newLng - lastFetchedCoords.lng) > 0.00001) {
                setLastFetchedCoords({ lat: newLat, lng: newLng });
                if (debounceTimer) clearTimeout(debounceTimer);
                const timer = setTimeout(() => {
                    fetchAddressFromCoords(newLat, newLng);
                }, 400);
                setDebounceTimer(timer);
            }
            setLat(newLat);
            setLng(newLng);
        }
    }

    return (
        <div className="flex w-full flex-col gap-6">
            <div className={`${card} flex flex-col justify-between gap-4 p-5 sm:flex-row sm:items-center sm:p-6`}>
                <div className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">Create your organization</div>
                <button
                    type="button"
                    onClick={attemptCreate}
                    disabled={mutation.isPending}
                    className={`${btnAccent} w-full flex-shrink-0 sm:w-auto`}
                >
                    {mutation.isPending ? 'Creating...' : 'Create Organization'}
                </button>
            </div>
            {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">{error}</div>}

            <FlatSection
                icon={<AboutSectionIcon />}
                title="About"
                subtitle="Bio, contact info & location"
            >
                <div>
                    <label className={labelCls}>Organization name *</label>
                    <Input classNames={heroInput} placeholder="Name" value={orgName} onChange={e => setOrgName(e.target.value)} onBlur={() => setTouched(t => ({ ...t, orgName: true }))} />
                    {touched.orgName && errors.orgName && <div className={errorText}>{errors.orgName}</div>}
                </div>
                <div>
                    <label className={labelCls}>About</label>
                    <Textarea
                        classNames={heroTextarea}
                        placeholder="Tell people about your organization..."
                        value={about}
                        onChange={e => setAbout(e.target.value)}
                        rows={5}
                    />
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                        <label className={labelCls}>Email *</label>
                        <Input classNames={heroInput} placeholder="e.g. Name@gmail.com" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(t => ({ ...t, email: true }))} />
                        {touched.email && errors.email && <div className={errorText}>{errors.email}</div>}
                    </div>
                    <div>
                        <label className={labelCls}>Phone</label>
                        <Input classNames={heroInput} placeholder="e.g. (777) 777-7777" value={phone} onChange={e => setPhone(e.target.value)} />
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Organization Address: <span className="text-red-500 dark:text-red-400">*</span></label>
                    {isLoaded && (
                        <div>
                            <Autocomplete
                                onLoad={onLoadAutocomplete}
                                onPlaceChanged={onPlaceChanged}
                                options={{
                                    componentRestrictions: { country: 'us' },
                                    types: ['establishment', 'geocode']
                                }}
                            >
                                <Input
                                    classNames={heroInput}
                                    className="mb-3 w-full"
                                    placeholder="Search for a place or address in the US"
                                    value={addressLoading ? 'Loading address...' : address}
                                    onChange={e => setAddress(e.target.value)}
                                    autoComplete="street-address"
                                />
                            </Autocomplete>
                            <div style={{ position: 'relative' }} className="overflow-hidden rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/10">
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    zoom={12}
                                    onLoad={onMapLoad}
                                    onIdle={handleMapIdle}
                                    center={{ lat, lng }}
                                >
                                    <Marker
                                        position={{ lat, lng }}
                                        icon={{
                                            url: '/marker.png',
                                            anchor: new window.google.maps.Point(16, 32),
                                        }}
                                    />
                                </GoogleMap>
                            </div>
                        </div>
                    )}
                </div>
            </FlatSection>

            <FlatSection
                icon={<VisualsSectionIcon />}
                title="Visuals"
                subtitle="Logo & cover photo"
            >
                <div>
                    <div className={labelCls}>Profile image</div>
                    <div className="mb-6 flex flex-wrap items-center gap-4">
                        <div className="relative">
                            <Avatar src={avatar} name={orgName} variant="org" size={64} className="ring-4 ring-white dark:ring-white/10" />
                        </div>
                        <div className="flex gap-2">
                            <button type="button" className={`${smallPill} bg-grey10 text-white hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90`} onClick={() => fileInputRef.current?.click()}>Upload</button>
                            <input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={handleAvatarChange} />
                            {avatar && (
                                <button type="button" className={`${smallPill} text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`} onClick={handleRemoveAvatar}>
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={labelCls}>Profile page banner photo <span className="text-xs font-medium text-grey50 dark:text-nightMuted">(Min 1024 × 464, Max 5MB)</span></div>
                    <div className="relative flex h-[140px] w-full flex-col items-center justify-center overflow-hidden rounded-[24px] border-2 border-dashed border-grey90 bg-grey99 text-center transition-colors hover:border-primary/40 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-nightAccent/40 sm:h-[200px]">
                        {banner ? (
                            <img src={banner} alt="Banner preview" className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                            <>
                                <svg width="40" height="40" fill="none" viewBox="0 0 40 40" className="mb-2"><rect x="8" y="12" width="24" height="16" rx="3" stroke="#B0B0B0" strokeWidth="2" /><path d="M8 28l7-7a2 2 0 012.8 0l4.2 4.2a2 2 0 002.8 0L32 20" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" /></svg>
                                <div className="text-sm font-semibold text-grey40 dark:text-nightMuted">Upload Image</div>
                            </>
                        )}
                        <input type="file" accept="image/*" ref={bannerInputRef} className="absolute inset-0 cursor-pointer opacity-0" onChange={handleBannerChange} />
                        {banner && (
                            <button type="button" className="absolute right-3 top-3 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-grey10 dark:bg-black/60 dark:text-white" onClick={handleRemoveBanner}>
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            </FlatSection>

            <FlatSection
                icon={<LinksSectionIcon />}
                title="Links"
                subtitle="Website & social media"
            >
                <div>
                    <label className={labelCls}>Organization handle</label>
                    <div className="flex h-14 items-center overflow-hidden rounded-2xl bg-grey99 ring-1 ring-black/[0.04] transition-shadow focus-within:ring-2 focus-within:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:focus-within:ring-nightAccent/60">
                        <span className="max-w-[55%] truncate whitespace-nowrap pl-4 text-sm text-grey50 dark:text-nightMuted">{window.location.host}/organization/</span>
                        <input
                            value={profileUrl}
                            onChange={e => setProfileUrl(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="your-organization"
                            className="h-full min-w-0 flex-1 bg-transparent pl-1 pr-4 text-base text-grey10 outline-none placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted"
                        />
                    </div>
                    <div className="mt-2 text-xs text-grey40 dark:text-nightMuted">Lowercase letters, numbers and hyphens only. Must be unique — this is your organization's public page address.</div>
                </div>
                <div className="flex flex-col gap-4">
                    <div>
                        <label className={labelCls}>Instagram</label>
                        <div className="flex items-center gap-3">
                            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10"><Instagram /></span>
                            <Input classNames={heroInput} placeholder="Instagram.com/username" value={instagram} onChange={e => setInstagram(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>X (Twitter)</label>
                        <div className="flex items-center gap-3">
                            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10"><Twitter /></span>
                            <Input classNames={heroInput} placeholder="X.com/username" value={twitter} onChange={e => setTwitter(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Linkedin</label>
                        <div className="flex items-center gap-3">
                            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10"><Linkedin /></span>
                            <Input classNames={heroInput} placeholder="Linkedin.com/in/username" value={linkedin} onChange={e => setLinkedin(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Facebook</label>
                        <div className="flex items-center gap-3">
                            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10"><Facebook /></span>
                            <Input classNames={heroInput} placeholder="Facebook.com/username" value={facebook} onChange={e => setFacebook(e.target.value)} />
                        </div>
                    </div>
                </div>
            </FlatSection>

            <FlatSection
                icon={<AccessSectionIcon />}
                title="Access"
                subtitle="Invite team members"
            >
                <div className="-mt-2 text-sm text-grey40 dark:text-nightMuted">
                    Add teammates now — they'll be invited automatically once your organization is created.
                </div>
                <div className="rounded-[24px] bg-grey99 p-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] sm:p-5">
                    <div className="space-y-5">
                        <div className="space-y-3">
                            <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Add New Authorization Email</div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Input
                                    type="email"
                                    placeholder="Enter email address"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    className="w-full"
                                    classNames={{ ...heroInput, inputWrapper: `${heroInput.inputWrapper} bg-white dark:bg-white/[0.06]` }}
                                />
                                <Select aria-label="Select" selectedKeys={[String(newRole ?? '')]} onChange={(e) => setNewRole(e.target.value as 'admin' | 'eventmanager' | 'usher')}>
                                    <SelectItem key="usher">Usher</SelectItem>
                                    <SelectItem key="eventmanager">Event Manager</SelectItem>
                                    <SelectItem key="admin">Admin</SelectItem>
                                </Select>
                            </div>
                            {accessError && <div className={errorText}>{accessError}</div>}
                            <button
                                type="button"
                                onClick={handleAddAuthEmail}
                                disabled={!newEmail}
                                className={`${btnAccent} h-10 w-full disabled:opacity-50 sm:w-auto`}
                            >
                                Add Email
                            </button>
                        </div>

                        {pendingAuthEmails.length > 0 ? (
                            <div className="space-y-3">
                                <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">Emails to invite</div>
                                {pendingAuthEmails.map((entry, index) => (
                                    <div key={entry.email} className="flex items-center justify-between rounded-2xl bg-white p-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                        {editingIndex === index ? (
                                            <div className="flex-1 space-y-3">
                                                <div className="break-all text-sm font-semibold text-grey10 dark:text-white">{entry.email}</div>
                                                <Select aria-label="Select" selectedKeys={[String(editingRole ?? '')]} onChange={(e) => setEditingRole(e.target.value as 'admin' | 'eventmanager' | 'usher')}>
                                                    <SelectItem key="usher">Usher</SelectItem>
                                                    <SelectItem key="eventmanager">Event Manager</SelectItem>
                                                    <SelectItem key="admin">Admin</SelectItem>
                                                </Select>
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={() => handleSaveAuthEmailRole(index)} className={`${smallPill} bg-primary text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg`}>Save</button>
                                                    <button type="button" onClick={() => setEditingIndex(null)} className={`${smallPill} text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                                                <div className="min-w-0 flex-1">
                                                    <div className="break-all text-sm font-semibold text-grey10 dark:text-white">{entry.email}</div>
                                                    <span className="mt-1 inline-flex rounded-full bg-grey99 px-2.5 py-0.5 text-xs font-semibold capitalize text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">{entry.role}</span>
                                                </div>
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setEditingIndex(index); setEditingRole(entry.role); }}
                                                        className={`${smallPill} text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAuthEmail(index)}
                                                        className={`${smallPill} bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20`}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-grey40 dark:text-nightMuted">No authorization emails staged yet.</div>
                        )}
                    </div>
                </div>
            </FlatSection>
        </div>
    );
}
